/**
 * The status line of a laptop calendar panel -- the Agenda's schedule
 * panel and a run's panel: "Sending…", then what the last action came
 * to ("Schedule paused. Its times and history are kept.", "Not paused:
 * ..."). A failure or a hold is an alert; the rest is said politely.
 *
 * It sits below the panel's buttons, which at 1280x720 can be the
 * bottom of what a run's panel shows: each new word brings it into view
 * (nearest edge, focus left where it is). One already said when the
 * line is drawn -- a panel drawn again -- is not news, and stays put.
 */
import { useEffect, useRef } from 'react';
import type { ScheduleActionStatus } from './useScheduleActions';

export function PanelStatus({
  status,
  testId,
}: {
  status: ScheduleActionStatus | null;
  testId: string;
}) {
  const ref = useRef<HTMLParagraphElement>(null);
  const said = status ? (status.pending ? 'pending' : (status.message ?? '')) : '';
  // What it already said when drawn: not news.
  const drawnWith = useRef(status !== null && !status.pending ? said : '');
  useEffect(() => {
    if (!said || said === drawnWith.current) return;
    drawnWith.current = '';
    ref.current?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [said]);

  if (!status || (!status.message && !status.pending)) return null;
  if (status.pending) {
    return (
      <p ref={ref} className="nx-lane-panel__note" role="status">
        Sending…
      </p>
    );
  }
  return (
    <p
      ref={ref}
      className={`nx-lane-panel__note${status.tone === 'error' ? ' is-fail' : status.tone === 'hold' ? ' is-hold' : ' is-ok'}`}
      role={status.tone === 'ok' ? 'status' : 'alert'}
      data-testid={testId}
    >
      {status.message}
    </p>
  );
}
